import type { Geometry2d, ImageShapeUtil, TLImageShape } from '@digitalsamba/tldraw';
import { getTldrawSync, type TldrawModule } from 'utils/whiteboard/tldrawRuntime';

type ImageShapeUtilClass = typeof ImageShapeUtil;

let InactiveImageUtilClass: ImageShapeUtilClass | null = null;

/**
 * Creates the InactiveImageUtil class dynamically after TLDraw is loaded.
 *
 * This is necessary because:
 * 1. InactiveImageUtil extends ImageShapeUtil (a TLDraw class)
 * 2. Class extension is evaluated at module load time
 * 3. We need TLDraw to be loaded first
 *
 * By creating the class inside a function, we defer the class definition
 * until TLDraw is actually available.
 */
export const createInactiveImageUtil = (tldraw: TldrawModule): ImageShapeUtilClass => {
  if (InactiveImageUtilClass) {
    return InactiveImageUtilClass;
  }

  const { ImageShapeUtil: BaseImageShapeUtil, Rectangle2d: Rect2d } = tldraw;

  InactiveImageUtilClass = class InactiveImageUtil extends BaseImageShapeUtil {
    static override type = 'inactive-image' as any;

    canEdit = () => false;

    canBind = () => false;

    canResize = () => false;

    canSelect = () => false;

    canSnap = () => false;

    isAspectRatioLocked = () => true;

    getGeometry(shape: TLImageShape): Geometry2d {
      return new Rect2d({
        width: shape.props.w,
        height: shape.props.h,
        isFilled: false,
      });
    }
  } as ImageShapeUtilClass;

  return InactiveImageUtilClass;
};

/**
 * Gets the InactiveImageUtil class, creating it if needed.
 * Only call this after TLDraw has been loaded!
 */
export const getInactiveImageUtil = (): ImageShapeUtilClass => {
  if (!InactiveImageUtilClass) {
    return createInactiveImageUtil(getTldrawSync());
  }
  return InactiveImageUtilClass;
};
